/**
 * /welcome — post-checkout confirmation (PUBLIC).
 *
 * Stripe Checkout success redirects here (PUBLIC_WEB_URL/welcome?session_id=…). The buyer has
 * paid but has no session yet — provisioning runs from the verified Stripe webhook and emails a
 * set-password link (which lands on /welcome/accept → /accept-invite). This screen reassures the
 * buyer and points them at their inbox + the web app.
 *
 * WO-M0 — built on the design system (Screen / Card / Button / Text + BeamLogo). Tokens only.
 */
import { StyleSheet, View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { Screen, Text, Card, Button } from '../../src/components/ui';
import { BeamLogo } from '../../src/components/brand/Logo';
import { theme } from '../../src/theme';

export default function WelcomeScreen() {
  const router = useRouter();
  const { session_id } = useLocalSearchParams<{ session_id?: string }>();
  const paid = Boolean(session_id);

  return (
    <Screen scroll maxWidth={520}>
      <View style={styles.brand}>
        <BeamLogo size={40} />
      </View>

      <Card style={styles.card}>
        <Text variant="title">{paid ? "You're in. Welcome to Beam." : 'Welcome to Beam'}</Text>
        <Text variant="body" color="secondary">
          {paid
            ? 'Your subscription is active and your workspace is being set up now.'
            : 'Your workspace is being set up.'}
        </Text>

        <View style={styles.steps}>
          <Step n="1" title="Check your email" body="We're sending a secure link to set your password and name your company." />
          <Step n="2" title="Set your password" body="Open the link to finish creating your owner account." />
          <Step n="3" title="Sign in & invite your crew" body="Add your team, import past jobs, and start capturing work." />
        </View>

        <Button title="Open the web app" size="lg" onPress={() => router.replace('/login')} accessibilityLabel="Open the web app to sign in" />
        <Text variant="small" color="secondary" style={styles.hint}>
          Didn't get the email within a few minutes? Check spam, or contact support and we'll help.
        </Text>
      </Card>
    </Screen>
  );
}

function Step({ n, title, body }: { n: string; title: string; body: string }) {
  return (
    <View style={styles.step}>
      <View style={styles.stepNum}>
        <Text variant="bodyStrong" color="onPrimary">
          {n}
        </Text>
      </View>
      <View style={styles.stepText}>
        <Text variant="bodyStrong">{title}</Text>
        <Text variant="small" color="secondary">
          {body}
        </Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  brand: { alignItems: 'center', marginTop: theme.spacing['2xl'] },
  card: { gap: theme.spacing.md },
  steps: { gap: theme.spacing.md, marginVertical: theme.spacing.sm },
  step: { flexDirection: 'row', gap: theme.spacing.md, alignItems: 'flex-start' },
  stepNum: {
    width: 28, height: 28, borderRadius: theme.radius.pill, alignItems: 'center', justifyContent: 'center',
    backgroundColor: theme.color.action.primary,
  },
  stepText: { flex: 1, gap: theme.spacing.xs },
  hint: { marginTop: theme.spacing.xs },
});
